import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { StoredEntry } from '@paradigm/desktop/src/utils/stored-entry';
import {
  FEATURE_UM_UI_OB_V1_API,
  FEATURE_UM_RFQ_HIDDEN_LIMIT_ORDER,
  FEATURE_UM_MULTI_PRODUCT_RFQ,
} from '@paradigm/utils/src/constants';
import { getUserTicker } from '@paradigm/store/auth';
import { FeatureFlags } from './adapters';
import { useIsFeatureFlagEnabled, useFeatureFlagVariant } from './store';

// Helper hook functions that are used to check if a feature flag is enabled.
export const useMarketIntegrityMarginFeatureFlag = () => {
  return useIsFeatureFlagEnabled('feature_market_integrity_margin');
};

/**
 * Feature flag for the streaming greeks feature
 * Remove this interface once the feature flag is removed
 */

export const useDbtAltCoinsObFeatureFlag = () => {
  return useIsFeatureFlagEnabled('feature_dbt_alt_coins_ob_auctions');
};

export const useOBV1APIFeatureFlag = () => {
  return useIsFeatureFlagEnabled(FEATURE_UM_UI_OB_V1_API);
};

export const useHiddenLimitOrderFeatureFlag = () => {
  const isBaseFeatureEnabled = useIsFeatureFlagEnabled(
    FEATURE_UM_RFQ_HIDDEN_LIMIT_ORDER,
  );
  const variant = useFeatureFlagVariant(FEATURE_UM_RFQ_HIDDEN_LIMIT_ORDER);
  const userTicker = useSelector(getUserTicker);

  if (!isBaseFeatureEnabled) {
    return false;
  }

  // If there's no variant or user ticker, use the base feature flag
  if (!variant || !userTicker) {
    return isBaseFeatureEnabled;
  }

  // If the tickers array is empty, enable for all
  const allowedTickers = normalizeTickers(variant.tickers);
  if (allowedTickers.length === 0) {
    return true;
  }

  // Check if the user's ticker is in the allowed list
  return allowedTickers.includes(userTicker);
};

/**
 * Returns the list of venues allowed to build multi-product (multi-underlying)
 * DRFQ v2 strategies, or [] if the feature is disabled or no venues are
 * configured.
 */
export function useMultiProductVenues(): readonly string[] {
  const isEnabled = useIsFeatureFlagEnabled(FEATURE_UM_MULTI_PRODUCT_RFQ);
  const variant = useFeatureFlagVariant(FEATURE_UM_MULTI_PRODUCT_RFQ);

  // Memoized so consumers can use the list as a hook dependency without
  // re-running on every render.
  return useMemo(() => {
    if (!isEnabled) {
      return NO_MULTI_PRODUCT_VENUES;
    }

    return normalizeVenues(variant?.venues);
  }, [isEnabled, variant]);
}

const NO_MULTI_PRODUCT_VENUES: readonly string[] = [];

export const featureFlagsMigrator = (state: string): FeatureFlags => {
  try {
    return JSON.parse(state) as FeatureFlags;
  } catch (error) {
    throw new TypeError('Error while parsing feature flags state');
  }
};

export const saveWhitelistedFlags = (
  flagsToPersist: string[],
  nextState: FeatureFlags,
  setter: StoredEntry<FeatureFlags>,
) => {
  const stateToSave = Object.fromEntries(
    Object.entries(nextState).filter(([key, _]) =>
      flagsToPersist.includes(key),
    ),
  );
  setter.set(JSON.stringify(stateToSave));
};

// Normalize tickers to a string array or empty array if invalid
const normalizeTickers = (tickers: unknown): string[] => {
  if (Array.isArray(tickers) && tickers.every((t) => typeof t === 'string')) {
    return tickers as string[];
  }
  if (tickers !== undefined) {
    console.warn(
      `Invalid tickers in ${FEATURE_UM_RFQ_HIDDEN_LIMIT_ORDER}. Expected string[], got:`,
      tickers,
    );
  }
  return [];
};

// Normalize venues to a string array or empty array if invalid
const normalizeVenues = (venues: unknown): string[] => {
  if (Array.isArray(venues) && venues.every((v) => typeof v === 'string')) {
    return venues as string[];
  }
  if (venues !== undefined) {
    console.warn(
      `Invalid venues in ${FEATURE_UM_MULTI_PRODUCT_RFQ}. Expected string[], got:`,
      venues,
    );
  }
  return [];
};

export const FLAGS_TO_PERSIST = [];
